const SUPPORTED_I18N_CODES = new Set(['en', 'fr', 'es', 'de', 'it'])

const stripDiacritics = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

/**
 * Normalize ISO code from API for i18next (public/locales/{code}/).
 * @param {string | null | undefined} code
 * @returns {'en' | 'fr' | 'es' | 'de' | 'it' | null}
 */
export function normalizeI18NLanguageCode (code) {
  if (!code || typeof code !== 'string') {
    return null
  }

  const normalized = code.trim().toLowerCase()
  return SUPPORTED_I18N_CODES.has(normalized) ? normalized : null
}

/**
 * Fallback when API has no code yet: map display name to i18next code.
 * @param {string | null | undefined} languageName
 * @returns {'en' | 'fr' | 'es' | 'de' | 'it' | null}
 */
export function resolveI18NLanguageCodeFromName (languageName) {
  if (!languageName || typeof languageName !== 'string') {
    return null
  }

  const normalizedLanguageName = stripDiacritics(languageName).trim().toLowerCase()
  if (normalizedLanguageName.startsWith('en')) return 'en'
  if (normalizedLanguageName.startsWith('fr')) return 'fr'
  if (normalizedLanguageName.startsWith('es')) return 'es'
  if (normalizedLanguageName.startsWith('de')) return 'de'
  if (normalizedLanguageName.startsWith('it')) return 'it'

  switch (normalizedLanguageName) {
    case 'english':
      return 'en'
    case 'french':
    case 'francais':
    case 'français':
      return 'fr'
    case 'spanish':
    case 'espanol':
    case 'español':
      return 'es'
    case 'german':
    case 'deutsch':
      return 'de'
    case 'italian':
    case 'italiano':
      return 'it'
    default:
      return null
  }
}

/**
 * Resolve i18next code from API language row or legacy name.
 * @param {{ code?: string, name?: string } | null | undefined} language
 * @returns {'en' | 'fr' | 'es' | 'de' | 'it' | null}
 */
export function resolveI18NLanguageCode (language) {
  if (!language) {
    return null
  }

  if (typeof language === 'string') {
    return normalizeI18NLanguageCode(language) || resolveI18NLanguageCodeFromName(language)
  }

  return normalizeI18NLanguageCode(language.code)
    || resolveI18NLanguageCodeFromName(language.name)
}

/**
 * Find language row id for an i18next ISO code.
 * @param {Record<string, { id: number, code?: string, name?: string }>} languagesById
 * @param {string} i18nCode
 * @returns {number | null}
 */
export function resolveLanguageIdFromI18NCode (languagesById, i18nCode) {
  if (!languagesById || !i18nCode) {
    return null
  }

  const normalized = i18nCode.trim().toLowerCase()
  const languages = Object.values(languagesById)
  const match = languages.find(
    ({ code, name }) => code?.toLowerCase() === normalized
      || resolveI18NLanguageCodeFromName(name) === normalized
  )
  if (match?.id == null) {
    return null
  }

  return typeof match.id === 'number' ? match.id : Number(match.id)
}
